import Avatar from "@material-ui/core/Avatar/Avatar";
import Chip from "@material-ui/core/Chip/Chip";
import CircularProgress from "@material-ui/core/CircularProgress/CircularProgress";
import MenuItem from "@material-ui/core/MenuItem/MenuItem";
import Paper from "@material-ui/core/Paper/Paper";
import TextField from "@material-ui/core/TextField/TextField";
import Typography from "@material-ui/core/Typography/Typography";
import withStyles from "@material-ui/core/styles/withStyles";
import Add from "@material-ui/icons/Add";
import Label from "@material-ui/icons/Label";
import Downshift from 'downshift';
import debounce from "lodash.debounce";
import * as React from "react";
import { TagModel } from "../../model/autogen/TagModel";

function isNew(tag: TagModel) {
    return tag.id == 0;
}

function getAvatar(tag: TagModel): JSX.Element {
    return <Avatar>
        {isNew(tag) ? <Add /> : <Label />}
    </Avatar>;
}

function renderInput(inputProps: any) {
    const { InputProps, classes, ref, ...other } = inputProps;

    return (
        <TextField
            InputProps={{
                inputRef: ref,
                classes: {
                    root: classes.inputRoot,
                },
                ...InputProps,
            }}
            {...other}
        />
    );
}

interface IRenderSuggestionOptions {
    highlightedIndex: number;
    index: number;
    itemProps: any;
    selectedItem: string;
    suggestion: TagModel;
}

function tagToString(tag: TagModel) {
    return tag.name;
}

function renderSuggestion({ suggestion, index, itemProps, highlightedIndex, selectedItem }: IRenderSuggestionOptions) {
    const isHighlighted = highlightedIndex === index;
    const isSelected = (selectedItem || '').indexOf(suggestion.name) > -1;

    return (
        <MenuItem
            {...itemProps}
            key={suggestion.name}
            selected={isHighlighted}
            component="div"
            style={{
                fontWeight: isSelected ? 500 : 400,
            }}>
            {isNew(suggestion) ? <><Add /> {`Add: "${suggestion.name}"`}</> : <><Label /> {suggestion.name}</>}
        </MenuItem>
    );
}

interface IDownshiftMultipleProps {
    isResourceOnly: boolean;
    initialTags: string[];
    onFetchTags: (query: string, isResourceOnly: boolean) => Promise<TagModel[]>;
    onResolveTags: (tags: string[]) => Promise<TagModel[]>;
    onTagsChanged: (list: TagModel[]) => void;
    classes: any;
    placeholder: string;
}

interface IDownshiftMultipleState {
    isDebouncing: boolean,
    inputValue: string;
    selectedItem: TagModel[];
    suggestions: TagModel[];
}

class DownshiftMultiple extends React.Component<IDownshiftMultipleProps, IDownshiftMultipleState> {
    constructor(props: IDownshiftMultipleProps) {
        super(props)
        this.state = {
            isDebouncing: false,
            inputValue: '',
            selectedItem: [],
            suggestions: []
        };
    }
    async componentDidMount() {
        if (this.props.onResolveTags) {
            const currentTags = [...this.state.selectedItem];
            const resolved = await this.props.onResolveTags(this.props.initialTags);
            this.setState({
                selectedItem: [...resolved, ...currentTags]
            });
        }
    }
    private debouncedSearch = debounce(async (search: string, isResourceOnly: boolean) => {
        const tags = await this.props.onFetchTags(search, isResourceOnly);
        this.setState({
            suggestions: tags,
            isDebouncing: false
        });
    }, 500);
    private getSuggestions(inputValue: string) {
        if (this.state.selectedItem.filter(si => si.name == inputValue).length > 0) {
            return [];
        }
        let count = 0;
        const items = this.state.suggestions.filter(suggestion => {
            const keep =
                (this.state.selectedItem.filter(si => si.name == suggestion.name).length == 0) &&
                (!inputValue || suggestion.name.toLowerCase().indexOf(inputValue.toLowerCase()) !== -1) &&
                count < 5;

            if (keep) {
                count += 1;
            }

            return keep;
        });
        return items;
    }
    private handleKeyDown = (event: React.KeyboardEvent<any>) => {
        const { inputValue, selectedItem } = this.state;
        if (selectedItem.length && !inputValue.length && event.key === 'backspace') {
            const newItems = [...selectedItem.slice(0, selectedItem.length - 1)];
            this.setState({
                selectedItem: newItems,
            }, () => {
                this.props.onTagsChanged(newItems);
            });
        }
        // handle the enter key
        if (event.key === 'enter') {
            console.log(`would add selected item or add ${inputValue}`);
        }
    };

    private handleInputChange = (event: any) => {
        this.setState({ inputValue: event.target.value, isDebouncing: true });
        this.debouncedSearch(event.target.value, this.props.isResourceOnly);
    };

    private handleChange = (item: any) => {
        let { selectedItem } = this.state;
        let broadcast = false;
        if (selectedItem.indexOf(item) === -1) {
            selectedItem = [...selectedItem, item];
            broadcast = true;
        }

        this.setState({
            inputValue: '',
            selectedItem,
        }, () => {
            if (broadcast) {
                this.props.onTagsChanged(selectedItem);
            }
        });
    };

    private handleDelete = (item: any) => () => {
        const selectedItem = [...this.state.selectedItem];
        selectedItem.splice(selectedItem.indexOf(item), 1);
        this.setState({ selectedItem });
        this.props.onTagsChanged(selectedItem);
    };

    render() {
        const { classes } = this.props;
        const { inputValue, selectedItem, isDebouncing } = this.state;

        return (
            <Downshift inputValue={inputValue} onChange={this.handleChange} selectedItem={selectedItem} itemToString={tagToString}>
                {({
                    getInputProps,
                    getItemProps,
                    isOpen,
                    inputValue: inputValue2,
                    selectedItem: selectedItem2,
                    highlightedIndex,
                }: any) => (
                        <div className={classes.container}>
                            {renderInput({
                                fullWidth: true,
                                classes,
                                InputProps: getInputProps({
                                    startAdornment: selectedItem.map(item => (
                                        <Chip
                                            key={item.name}
                                            avatar={getAvatar(item)}
                                            tabIndex={-1}
                                            label={item.name}
                                            className={classes.chip}
                                            onDelete={this.handleDelete(item)}
                                        />
                                    )),
                                    onChange: this.handleInputChange,
                                    onKeyDown: this.handleKeyDown,
                                    placeholder: this.props.placeholder,
                                    id: 'integration-downshift-multiple',
                                }),
                            })}
                            {isOpen ? (isDebouncing ? (
                                <Paper className={classes.paper} square>
                                    <Typography><CircularProgress size={24} /> Finding suggestions ...</Typography>
                                </Paper>
                            ) : (

                                    <Paper style={{ zIndex: 50 }} className={classes.paper} square>
                                        {this.getSuggestions(inputValue2).map((suggestion, index) =>
                                            renderSuggestion({
                                                suggestion,
                                                index,
                                                itemProps: getItemProps({ item: suggestion }),
                                                highlightedIndex,
                                                selectedItem: selectedItem2,
                                            }),
                                        )}
                                    </Paper>
                                )) : null}
                        </div>
                    )}
            </Downshift>
        );
    }
}

const styles = (theme: any) => ({
    root: {
        flexGrow: 1,
        marginTop: "10px",
        width: "100%"
    },
    container: {
        flexGrow: 1,
        position: 'relative',
    },
    paper: {
        position: 'absolute',
        zIndex: 1,
        marginTop: theme.spacing.unit,
        left: 0,
        right: 0,
    },
    chip: {
        margin: `${theme.spacing.unit / 2}px ${theme.spacing.unit / 4}px`,
    },
    inputRoot: {
        flexWrap: 'wrap',
    },
});

export interface ITagListEditorProps {
    isResourceOnly: boolean;
    initialTags: string[];
    onFetchTags: (query: string, isResourceOnly: boolean) => Promise<TagModel[]>;
    onResolveTags: (tags: string[]) => Promise<TagModel[]>;
    onTagsChanged: (list: TagModel[]) => void;
    classes: any;
}

function TagListEditor(props: ITagListEditorProps) {
    const { classes, onTagsChanged, onFetchTags, onResolveTags, initialTags } = props;
    return (
        <div className={classes.root}>
            {/*
            <Downshift>
                {({ getInputProps, getItemProps, isOpen, inputValue, selectedItem, highlightedIndex }: any) => (
                    <div className={classes.container}>
                        {renderInput({
                            fullWidth: true,
                            classes,
                            InputProps: getInputProps({
                                placeholder: 'Search a country (start with a)',
                                id: 'integration-downshift-simple',
                            }),
                        })}
                        {isOpen ? (
                            <Paper className={classes.paper} square>
                                {getSuggestions(inputValue).map((suggestion, index) =>
                                    renderSuggestion({
                                        suggestion,
                                        index,
                                        itemProps: getItemProps({ item: suggestion.label }),
                                        highlightedIndex,
                                        selectedItem,
                                    }),
                                )}
                            </Paper>
                        ) : null}
                    </div>
                )}
            </Downshift>
            */}
            <DownshiftMultiple isResourceOnly={props.isResourceOnly} initialTags={initialTags} classes={classes} onResolveTags={onResolveTags} onTagsChanged={onTagsChanged} onFetchTags={onFetchTags} placeholder="Select tags" />
        </div>
    );
}

export default withStyles(styles as any)(TagListEditor);


// WEBPACK FOOTER //
// ./src/components/material/tag-list-editor.tsx